import { captureException } from '@sentry/react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { type ResolvableModalFC } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { YStack } from '@/components/ui/layout/layout';
import { getEnvVar } from '@/env';

import { teamApis } from '../team.query';

const BAA_PDF_URL = `${getEnvVar('VITE_API_URL')}/static/littlebird-baa.pdf`;

interface TeamBaaAgreementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resolve: (enabled: boolean) => void;
  teamId: string;
}

export const TeamBaaAgreementModal: ResolvableModalFC<
  TeamBaaAgreementModalProps,
  boolean
> = ({ open, onOpenChange, resolve, teamId }) => {
  const [legalName, setLegalName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasResolved = useRef(false);

  const trimmedName = legalName.trim();
  const canSubmit = trimmedName.length > 0 && !isSubmitting;

  const finish = (enabled: boolean) => {
    hasResolved.current = true;
    resolve(enabled);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && !hasResolved.current) finish(false);
    onOpenChange(next);
  };

  const handleAgree = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await teamApis.enableBaa(teamId, trimmedName);
      toast.success('HIPAA enforced for all team members');
      onOpenChange(false);
      finish(true);
    } catch (error) {
      captureException(error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to enable HIPAA'
      );
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content className="gap-4 max-w-md">
        <Dialog.Header className="text-left">
          <Dialog.Title>HIPAA Business Associate Agreement</Dialog.Title>
        </Dialog.Header>

        <YStack className="gap-3">
          <p className="text-sm text-foreground">
            Click here to view the{' '}
            <a
              href={BAA_PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-accent hover:text-accent/80"
            >
              HIPAA Business Associate Agreement
            </a>
            .
          </p>

          <p className="text-sm text-danger-9">
            *By clicking "I agree", you agree to the HIPAA Business Associate
            Agreement on behalf of your team. This action cannot be undone and
            will enforce privacy mode for every current and future team member.
          </p>

          <Input
            autoFocus
            placeholder="Enter your legal name"
            value={legalName}
            disabled={isSubmitting}
            onChange={(e) => setLegalName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit) handleAgree();
            }}
          />
        </YStack>

        <Dialog.Footer className="mt-1">
          <Button
            className="w-full"
            disabled={!canSubmit}
            onClick={handleAgree}
          >
            {isSubmitting ? 'Enabling...' : 'I agree'}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
